import React, { useState, useEffect } from 'react';
import { Search, Plus, X, Loader2, Edit2, Trash2, ExternalLink, Link as LinkIcon, Image as ImageIcon } from 'lucide-react';

export default function HeroSliderManagement() {
  const [slidersList, setSlidersList] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [tableLoading, setTableLoading] = useState(false);

  // Pagination Parameters
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 5;

  // Modal State Controls
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);

  // Image Upload File States & Local Preview URLs
  const [sliderImageFile, setSliderImageFile] = useState(null);
  const [sliderImagePreview, setSliderImagePreview] = useState(null);

  const [headerLogoFile, setHeaderLogoFile] = useState(null);
  const [headerLogoPreview, setHeaderLogoPreview] = useState(null);

  // Form State
  const [sliderForm, setSliderForm] = useState({
    heading: '',
    description: '',
    cta_text: 'Explore Programs',
    cta_link: '#',
    display_order: '1',
    slider_image_url: '',
    header_logo_url: ''
  });

  useEffect(() => {
    fetchSliders();
  }, [searchTerm]);

  const fetchSliders = async () => {
    setTableLoading(true);
    try {
      const res = await fetch(`https://metapi.emedhatechnologies.com/api/hero-sliders?search=${encodeURIComponent(searchTerm)}`);
      const result = await res.json();
      if (result.success) setSlidersList(result.data);
      else setSlidersList([]);
    } catch (err) {
      console.error(err);
      setSlidersList([]);
    } finally {
      setTableLoading(false);
    }
  };

  // Image Change Handlers with URL Preview Memory Cleanup
  const handleSliderImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSliderImageFile(file);
      setSliderImagePreview(URL.createObjectURL(file));
    }
  };

  const handleHeaderLogoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setHeaderLogoFile(file);
      setHeaderLogoPreview(URL.createObjectURL(file));
    }
  };

  const handleRemoveSliderImage = () => {
    setSliderImageFile(null);
    setSliderImagePreview(null);
    setSliderForm(prev => ({ ...prev, slider_image_url: '' }));
  };

  const handleRemoveHeaderLogo = () => {
    setHeaderLogoFile(null);
    setHeaderLogoPreview(null);
    setSliderForm(prev => ({ ...prev, header_logo_url: '' }));
  };

  const handleOpenAddModal = () => {
    setEditingId(null);
    setSliderImageFile(null);
    setSliderImagePreview(null);
    setHeaderLogoFile(null);
    setHeaderLogoPreview(null);
    setSliderForm({
      heading: '',
      description: '',
      cta_text: 'Explore Programs',
      cta_link: '#',
      display_order: (slidersList.length + 1).toString(),
      slider_image_url: '',
      header_logo_url: ''
    });
    setIsModalOpen(true);
  };

  const handleOpenEditModal = (item) => {
    setEditingId(item.id);
    setSliderImageFile(null);
    setHeaderLogoFile(null);

    setSliderImagePreview(item.slider_image_url || null);
    setHeaderLogoPreview(item.header_logo_url || null);

    setSliderForm({
      heading: item.heading,
      description: item.description || '',
      cta_text: item.cta_text || 'Explore Programs',
      cta_link: item.cta_link || '#',
      display_order: item.display_order ? item.display_order.toString() : '1',
      slider_image_url: item.slider_image_url || '',
      header_logo_url: item.header_logo_url || ''
    });

    setIsModalOpen(true);
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();

    const formData = new FormData();
    formData.append('payload', JSON.stringify(sliderForm));
    if (sliderImageFile) {
      formData.append('slider_image', sliderImageFile);
    }
    if (headerLogoFile) {
      formData.append('header_logo', headerLogoFile);
    }

    const endpoint = editingId ? `update-slider/${editingId}` : 'add-slider';
    const method = editingId ? 'PUT' : 'POST';

    try {
      const res = await fetch(`https://metapi.emedhatechnologies.com/api/hero-sliders/${endpoint}`, {
        method: method,
        body: formData
      });
      const result = await res.json();
      if (result.success) {
        setIsModalOpen(false);
        fetchSliders();
      } else {
        alert(result.message || "Operation failed.");
      }
    } catch (err) {
      console.error(err);
    }
  };

  const handleDeleteEntry = async (id) => {
    if (!window.confirm("Are you sure you want to remove this hero slider?")) return;
    try {
      const res = await fetch(`https://metapi.emedhatechnologies.com/api/hero-sliders/delete-slider/${id}`, {
        method: 'PUT'
      });
      if ((await res.json()).success) fetchSliders();
    } catch (err) {
      console.error(err);
    }
  };

  // Pagination Range Slices
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = slidersList.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(slidersList.length / itemsPerPage);

  return (
    <div style={styles.container}>
      
      {/* TOP HEADER CTA BAR */}
      <div style={styles.topActionBar}>
        <div>
          <h2 style={styles.mainTitle}>Hero Sliders Management</h2>
          <p style={styles.mainSubtitle}>Configure landing page top showcase banners, headers, logos, and descriptions</p>
        </div>
        <button onClick={handleOpenAddModal} style={styles.addBtn}>
          <Plus size={15} style={{ marginRight: '6px' }} /> Add New Hero Slider
        </button>
      </div>

      {/* SEARCH AND RECORD COUNT STRIP */}
      <div style={styles.filterControlPanel}>
        <div style={styles.searchWrapper}>
          <Search size={16} style={styles.searchIcon} />
          <input
            type="text"
            placeholder="Search by heading or description..."
            value={searchTerm}
            onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
            style={styles.tableSearchInput}
          />
        </div>
        <div style={styles.totalIndicator}>Active Banners: <strong style={{ color: '#1c6ec9' }}>{slidersList.length}</strong></div>
      </div>

      {/* MASTER DATA TABLE */}
      {tableLoading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '40px' }}>
          <Loader2 size={32} style={{ animation: 'spin 1s linear infinite' }} color="#1c6ec9" />
        </div>
      ) : (
        <div style={{ overflowX: 'auto', width: '100%' }}>
          <table style={styles.dataTable}>
            <thead>
              <tr>
                <th style={styles.tableTh}>Order</th>
                <th style={styles.tableTh}>Slider Banner Background</th>
                <th style={styles.tableTh}>Header Logo & Heading Title</th>
                <th style={styles.tableTh}>Description</th>
                <th style={styles.tableTh}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {slidersList.length === 0 ? (
                <tr><td colSpan="5" style={styles.noDataTd}>No hero sliders uploaded yet.</td></tr>
              ) : (
                currentItems.map((item) => (
                  <tr key={item.id} style={styles.tableTr}>
                    <td style={{ ...styles.tableTd, textAlign: 'center', fontWeight: '700' }}>
                      <span style={styles.orderBadge}>#{item.display_order}</span>
                    </td>
                    <td style={styles.tableTd}>
                      {item.slider_image_url ? (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <img src={item.slider_image_url} alt="Slider" style={styles.bannerPreviewImg} />
                          <a href={item.slider_image_url} target="_blank" rel="noreferrer" style={styles.iconLink}>
                            <ExternalLink size={12} />
                          </a>
                        </div>
                      ) : (
                        <span style={{ color: '#94a3b8', fontSize: '11px' }}>No Image</span>
                      )}
                    </td>
                    <td style={styles.tableTd}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        {item.header_logo_url && (
                          <img src={item.header_logo_url} alt="Logo" style={styles.logoPreviewImg} />
                        )}
                        <strong style={{ color: '#0f172a', fontSize: '13.5px' }}>{item.heading}</strong>
                      </div>
                    </td>
                    <td style={styles.tableTd}>
                      <div style={{ maxWidth: '240px', whiteSpace: 'normal', fontSize: '12px', color: '#64748b' }}>
                        {item.description || 'N/A'}
                      </div>
                    </td>
                    <td style={styles.tableTd}>
                      <div style={styles.actionBtnGroup}>
                        <button onClick={() => handleOpenEditModal(item)} style={styles.editRowBtn} title="Edit Slider"><Edit2 size={12} /> Edit</button>
                        <button onClick={() => handleDeleteEntry(item.id)} style={styles.deleteRowBtn} title="Delete Slider"><Trash2 size={12} /> Delete</button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* PAGINATION TOOLBAR */}
      {slidersList.length > 0 && (
        <div style={styles.paginationDock}>
          <button
            disabled={currentPage === 1}
            onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
            style={currentPage === 1 ? { ...styles.pageBtn, ...styles.pageBtnDisabled } : styles.pageBtn}
          >
            Previous
          </button>
          <span style={styles.pageIndicator}>
            Page <strong>{currentPage}</strong> of {totalPages || 1}
          </span>
          <button
            disabled={currentPage === totalPages || totalPages === 0}
            onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
            style={currentPage === totalPages || totalPages === 0 ? { ...styles.pageBtn, ...styles.pageBtnDisabled } : styles.pageBtn}
          >
            Next
          </button>
        </div>
      )}

      {/* POPUP MANAGEMENT MODAL */}
      {isModalOpen && (
        <div style={styles.modalBackdrop}>
          <div style={styles.modalWrapperCard}>
            <div style={styles.modalHeader}>
              <h3 style={{ margin: 0, fontSize: '15px', fontWeight: '700', color: '#0f172a' }}>
                {editingId ? 'Modify Hero Slider Entry' : 'Create New Hero Slider'}
              </h3>
              <button onClick={() => setIsModalOpen(false)} style={styles.modalCloseX}><X size={18} /></button>
            </div>

            <form onSubmit={handleFormSubmit} style={styles.modalForm}>
              
              {/* MAIN SLIDER IMAGE FIELD WITH LIVE PREVIEW & REMOVE ICON */}
              <div style={styles.fieldContainer}>
                <label style={styles.fieldLabel}>Main Slider Image *</label>
                {sliderImagePreview ? (
                  <div style={styles.previewContainer}>
                    <img src={sliderImagePreview} alt="Main Slider Preview" style={styles.mainImagePreview} />
                    <button type="button" onClick={handleRemoveSliderImage} style={styles.removeImageBtn} title="Remove Main Image">
                      <X size={14} />
                    </button>
                  </div>
                ) : (
                  <input
                    type="file"
                    accept="image/*"
                    required={!editingId}
                    onChange={handleSliderImageChange}
                    style={styles.formFileInput}
                  />
                )}
              </div>

              {/* HEADING TITLE */}
              <div style={styles.fieldContainer}>
                <label style={styles.fieldLabel}>Slider Heading Title *</label>
                <input
                  type="text"
                  required
                  placeholder="e.g. Empowering Higher Distance Education"
                  value={sliderForm.heading}
                  onChange={(e) => setSliderForm({ ...sliderForm, heading: e.target.value })}
                  style={styles.formInput}
                />
              </div>

              {/* DESCRIPTION TEXTAREA */}
              <div style={styles.fieldContainer}>
                <label style={styles.fieldLabel}>Banner Description Text</label>
                <textarea
                  rows="3"
                  placeholder="Enter detailed banner description text..."
                  value={sliderForm.description}
                  onChange={(e) => setSliderForm({ ...sliderForm, description: e.target.value })}
                  style={{ ...styles.formInput, fontFamily: 'inherit', resize: 'vertical' }}
                />
              </div>

              {/* HEADER LOGO FIELD WITH LIVE PREVIEW & REMOVE ICON */}
              <div style={styles.fieldContainer}>
                <label style={styles.fieldLabel}>Header Small Logo (Beside Heading)</label>
                {headerLogoPreview ? (
                  <div style={styles.previewContainer}>
                    <img src={headerLogoPreview} alt="Header Logo Preview" style={styles.logoImagePreview} />
                    <button type="button" onClick={handleRemoveHeaderLogo} style={styles.removeImageBtn} title="Remove Header Logo">
                      <X size={14} />
                    </button>
                  </div>
                ) : (
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleHeaderLogoChange}
                    style={styles.formFileInput}
                  />
                )}
              </div>

              {/* DISPLAY ORDER */}
              <div style={styles.fieldContainer}>
                <label style={styles.fieldLabel}>Display Sequence Order Number</label>
                <input
                  type="number"
                  min="1"
                  required
                  value={sliderForm.display_order}
                  onChange={(e) => setSliderForm({ ...sliderForm, display_order: e.target.value })}
                  style={styles.formInput}
                />
              </div>

              <div style={styles.formActionDock}>
                <button type="button" onClick={() => setIsModalOpen(false)} style={styles.formCancelBtn}>Cancel</button>
                <button type="submit" style={styles.formSaveBtn}>Save Hero Slider</button>
              </div>

            </form>
          </div>
        </div>
      )}

    </div>
  );
}

// FORMATTING STYLES OBJECT MATRIX
const styles = {
  container: { width: '100%', boxSizing: 'border-box' },
  topActionBar: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '16px', marginBottom: '20px', flexWrap: 'wrap' },
  mainTitle: { margin: 0, fontSize: '20px', fontWeight: '800', color: '#0f172a' },
  mainSubtitle: { margin: '2px 0 0 0', fontSize: '13px', color: '#64748b' },
  addBtn: { background: 'linear-gradient(90deg, #e30504 0%, #a80302 100%)', border: 'none', borderRadius: '12px', color: '#fff', padding: '12px 20px', fontSize: '13px', fontWeight: '700', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', whiteSpace: 'nowrap' },
  filterControlPanel: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '16px', marginBottom: '16px', flexWrap: 'wrap', borderBottom: '1px solid #edf2f7', paddingBottom: '14px' },
  searchWrapper: { position: 'relative', display: 'flex', alignItems: 'center', flex: 1, maxWidth: '340px', width: '100%' },
  searchIcon: { position: 'absolute', left: '12px', color: '#94a3b8' },
  tableSearchInput: { width: '100%', padding: '9px 12px 9px 36px', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '9px', color: '#0f172a', fontSize: '13.5px', outline: 'none' },
  totalIndicator: { fontSize: '14.5px', fontWeight: '600', color: '#334155' },
  dataTable: { width: '100%', borderCollapse: 'collapse', textAlign: 'left' },
  tableTh: { padding: '12px 14px', borderBottom: '2px solid #e2e8f0', color: '#64748b', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.5px', fontWeight: '600', whiteSpace: 'nowrap' },
  tableTr: { borderBottom: '1px solid #f1f5f9', background: '#ffffff' },
  tableTd: { padding: '12px 14px', color: '#475569', fontSize: '13px', verticalAlign: 'middle', whiteSpace: 'nowrap' },
  noDataTd: { padding: '30px 14px', textAlign: 'center', color: '#94a3b8', fontSize: '14px', fontWeight: '500' },
  orderBadge: { background: '#f1f5f9', border: '1px solid #cbd5e1', color: '#475569', padding: '3px 8px', borderRadius: '6px', fontSize: '12px', fontWeight: '700' },
  bannerPreviewImg: { width: '80px', height: '42px', borderRadius: '6px', objectFit: 'cover', border: '1px solid #e2e8f0' },
  logoPreviewImg: { width: '28px', height: '28px', borderRadius: '50%', objectFit: 'cover', border: '1px solid #e2e8f0' },
  iconLink: { color: '#1c6ec9', display: 'inline-flex', alignItems: 'center' },
  actionBtnGroup: { display: 'flex', gap: '6px', alignItems: 'center' },
  editRowBtn: { background: '#f0fdf4', border: '1px solid #bbf7d0', color: '#166534', padding: '5px 10px', borderRadius: '6px', fontSize: '11.5px', fontWeight: '600', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '3px' },
  deleteRowBtn: { background: '#fff1f2', border: '1px solid #fecaca', color: '#b91c1c', padding: '5px 10px', borderRadius: '6px', fontSize: '11.5px', fontWeight: '600', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '3px' },
  modalBackdrop: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(15, 23, 42, 0.25)', backdropFilter: 'blur(8px)', zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' },
  modalWrapperCard: { background: '#ffffff', border: '1px solid rgba(255,255,255,0.8)', borderRadius: '20px', width: '100%', maxWidth: '580px', maxHeight: '90vh', padding: '24px', display: 'flex', flexDirection: 'column', boxSizing: 'border-box', boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.05)', overflowY: 'auto' },
  modalHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingBottom: '12px', borderBottom: '1px solid #e2e8f0' },
  modalCloseX: { background: 'transparent', border: 'none', color: '#94a3b8', cursor: 'pointer' },
  modalForm: { display: 'flex', flexDirection: 'column', gap: '12px', marginTop: '14px' },
  fieldContainer: { display: 'flex', flexDirection: 'column', gap: '4px' },
  fieldLabel: { color: '#64748b', fontSize: '11px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px' },
  formInput: { width: '100%', padding: '9px 12px', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '8px', color: '#0f172a', fontSize: '13px', outline: 'none', boxSizing: 'border-box' },
  formFileInput: { fontSize: '12px', color: '#475569', padding: '6px 0' },
  
  // PREVIEW CONTAINER & REMOVE BUTTON STYLES
  previewContainer: { position: 'relative', display: 'inline-block', width: 'fit-content', marginTop: '4px' },
  mainImagePreview: { width: '100%', maxHeight: '140px', borderRadius: '8px', objectFit: 'cover', border: '1px solid #cbd5e1' },
  logoImagePreview: { width: '50px', height: '50px', borderRadius: '8px', objectFit: 'cover', border: '1px solid #cbd5e1' },
  removeImageBtn: { position: 'absolute', top: '-8px', right: '-8px', background: '#e30504', color: '#ffffff', border: 'none', borderRadius: '50%', width: '22px', height: '22px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', boxShadow: '0 2px 6px rgba(0,0,0,0.2)' },

  formActionDock: { display: 'flex', justifyContent: 'flex-end', gap: '12px', marginTop: '8px', borderTop: '1px solid #e2e8f0', paddingTop: '12px' },
  formCancelBtn: { background: 'transparent', border: '1px solid #e2e8f0', color: '#64748b', padding: '9px 16px', borderRadius: '8px', cursor: 'pointer' },
  formSaveBtn: { background: 'linear-gradient(90deg, #10b981 0%, #059669 100%)', border: 'none', color: '#fff', padding: '9px 16px', borderRadius: '8px', fontWeight: '600', cursor: 'pointer' },
  paginationDock: { display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: '12px', marginTop: '16px', paddingTop: '12px', borderTop: '1px solid #edf2f7' },
  pageIndicator: { fontSize: '13px', color: '#475569' },
  pageBtn: { background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '6px', padding: '6px 14px', color: '#334155', fontSize: '12.5px', fontWeight: '600', cursor: 'pointer' },
  pageBtnDisabled: { background: '#f8fafc', color: '#94a3b8', cursor: 'not-allowed', borderColor: '#e2e8f0' }
};